import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, CalendarDays, Ticket, ClipboardList, Users,
  ScanLine, Mail, BarChart3, Settings, Shield, LogOut, Palette, Compass, Megaphone, UserPlus, Map,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { ScareLogo } from "./Brand";
import { PlatformBanners } from "./PlatformBanners";
import EventSwitcher from "./EventSwitcher";
import InstallPrompt from "./InstallPrompt";
import { MobileTopBar, MobileBottomNav } from "./MobileNav";

const NAV = [
  { to: "/dashboard/overview", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/dashboard/open-days", label: "Open Days", icon: CalendarDays, testid: "nav-open-days" },
  { to: "/dashboard/tickets", label: "Tickets", icon: Ticket, testid: "nav-tickets" },
  { to: "/dashboard/orders", label: "Orders", icon: ClipboardList, testid: "nav-orders" },
  { to: "/dashboard/attendees", label: "Attendees", icon: Users, testid: "nav-attendees" },
  { to: "/dashboard/guest-lists", label: "Guest Lists", icon: UserPlus, testid: "nav-guest-lists" },
  { to: "/dashboard/marketing", label: "Marketing", icon: Megaphone, testid: "nav-marketing" },
  { to: "/dashboard/checkin", label: "Check-In", icon: ScanLine, testid: "nav-checkin" },
  { to: "/dashboard/reports", label: "Reports", icon: BarChart3, testid: "nav-reports" },
  { to: "/dashboard/reach", label: "Reach", icon: Map, testid: "nav-reach" },
];

const SECONDARY = [
  { to: "/dashboard/branding", label: "Branding", icon: Palette, testid: "nav-branding" },
  { to: "/dashboard/emails", label: "Email Templates", icon: Mail, testid: "nav-emails" },
  { to: "/dashboard/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
];

export default function DashboardLayout({ children, title, subtitle, actions }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  return (
    <div className="st-dashboard min-h-screen lg:flex">
      {/* Sidebar (desktop only) */}
      <aside className="hidden lg:flex w-[220px] bg-[#0c0c0c] border-r border-[#1a1a1a] flex-col p-4 sticky top-0 h-screen">
        <button onClick={() => nav("/dashboard/overview")} className="text-left" data-testid="brand-button">
          <ScareLogo size={24} />
        </button>

        <div className="mt-6">
          <EventSwitcher />
        </div>

        <nav className="mt-6 flex flex-col gap-1.5" data-testid="sidebar-nav">
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to} to={n.to} data-testid={n.testid}
                className={({ isActive }) => `st-sidebar-link ${isActive ? "active" : ""}`}
              >
                <Icon size={18} /><span>{n.label}</span>
              </NavLink>
            );
          })}

          <div className="my-3 h-px bg-[#1a1a1a]" />

          {SECONDARY.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to} to={n.to} data-testid={n.testid}
                className={({ isActive }) => `st-sidebar-link ${isActive ? "active" : ""}`}
              >
                <Icon size={18} /><span>{n.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="mt-auto pt-6 border-t border-[#1a1a1a]">
          <button
            onClick={() => nav("/dashboard/open-days?tour=force")}
            className="st-sidebar-link w-full mb-3 text-[13px]"
            data-testid="take-a-tour"
          >
            <Compass size={18} /><span>Take a tour</span>
          </button>
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate" data-testid="sidebar-username">{user?.name || "Admin"}</div>
              <div className="text-xs text-[#8a8a8a] truncate">{user?.email}</div>
            </div>
            <button
              onClick={async () => { await logout(); nav("/login"); }}
              className="text-[#dc2626] hover:text-[#ef4444]"
              data-testid="logout-btn" title="Sign out"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 px-4 lg:px-7 py-4 lg:py-6 pb-24 lg:pb-6 min-w-0">
        <MobileTopBar />
        <PlatformBanners />
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-6 pb-4 border-b border-[#1a1a1a] mt-4 lg:mt-0">
          <div>
            <h1 className="text-lg sm:text-[22px] font-semibold tracking-tight">{title}</h1>
            {subtitle && <p className="text-[#8a8a8a] text-[13px] mt-1">{subtitle}</p>}
          </div>
          {actions && <div className="shrink-0">{actions}</div>}
        </div>
        {children}
      </main>
      <MobileBottomNav />
      <InstallPrompt />
    </div>
  );
}
